import { FC, memo, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Col, Row, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DoctorAppointmentStatistics } from 'gql/statistics/__generated__/DoctorAppointmentStatistics';
import { GET_DOCTOR_APPOINTMENT_STATISTICS } from 'gql/statistics/queries';

import { Loading } from 'components/ui';
import AgeGroup from './AgeGroup';
import AppointmentsGender from './AppointmentsGender';
import AppointmentsMonthly from './AppointmentsMonthly';
import PageView from './PageView';

const { Title } = Typography;

const StyledCharts = styled.div`
  margin-top: 40px;
  .chart-item-card {
    position: relative;
    &::after {
      content: '';
      position: absolute;
      z-index: 10;
      left: 24px;
      bottom: 24px;
      height: 20px;
      width: 100px;
      background-color: #ffffff;
    }
  }
`;

const Charts: FC = () => {
  const { t } = useTranslation();
  const { data, loading } = useQuery<DoctorAppointmentStatistics>(
    GET_DOCTOR_APPOINTMENT_STATISTICS,
    {
      fetchPolicy: 'cache-only'
    }
  );

  const result = useMemo(
    () => data?.doctorAppointmentStatistics?.data,
    [data?.doctorAppointmentStatistics?.data]
  );

  if (loading || !result) {
    return <Loading />;
  }

  return (
    <StyledCharts>
      <Title level={4}>{t('dashboard.charts')}</Title>
      <Row gutter={[32, 32]} align="stretch" wrap>
        <Col xs={24} lg={12} className="chart-item-card">
          <AppointmentsMonthly
            total={result?.appointmentsCount || 0}
            data={
              result.dateGroup?.map(item => ({
                date: item.month,
                value: +item.count
              })) || []
            }
          />
        </Col>
        <Col xs={24} lg={12} className="chart-item-card">
          <AgeGroup
            total={result?.appointmentsCount || 0}
            data={
              result.ageGroup?.map(item => ({
                value: +item.count,
                category: item.age
              })) || []
            }
          />
        </Col>
        <Col xs={24} lg={12} className="chart-item-card">
          <AppointmentsGender
            total={result?.appointmentsCount || 0}
            data={[
              {
                category: t('dashboard.male'),
                value: Number(result?.gender?.Male?.count || 0)
              },
              {
                category: t('dashboard.female'),
                value: Number(result?.gender?.Female?.count || 0)
              }
            ]}
          />
        </Col>
        <Col xs={24} lg={12} className="chart-item-card">
          <PageView />
        </Col>
      </Row>
    </StyledCharts>
  );
};

export default memo(Charts);
